/*
 * Copyright (c) 2020, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */
import { ownerDocumentGetter } from '../env/node';
import { defaultViewGetter } from '../env/document';
import { shadowRootGetter } from '../env/element';

// Helpful for tests running with jsdom
export function getOwnerDocument(node: Node): Document {
  const doc = ownerDocumentGetter.call(node);
  // if doc is null, it means `this` is actually a document instance
  return doc === null ? (node as Document) : doc;
}

export function getOwnerWindow(node: Node): Window {
  const doc = getOwnerDocument(node);
  const win = defaultViewGetter.call(doc);
  if (win === null) {
    // this method should never be called with a node that is not part
    // of a qualifying connected node.
    throw new TypeError();
  }
  return win;
}

/**
 * This utility should be used to convert NodeList and HTMLCollection into an array before we
 * perform array operations on them. See issue #1545 for more details.
 */
export function arrayFromCollection<T extends Node, K extends Element>(
  collection: NodeListOf<T> | HTMLCollectionOf<K>
): Array<T | K> {
  const size = collection.length;
  const cloned: T[] | K[] = [];
  if (size > 0) {
    for (let i = 0; i < size; i++) {
      cloned[i] = collection[i];
    }
  }
  return cloned;
}

const {
  assign,
  create,
  defineProperties,
  defineProperty,
  freeze,
  getOwnPropertyDescriptor,
  getOwnPropertyDescriptors,
  getOwnPropertyNames,
  getPrototypeOf,
  keys,
  seal,
  setPrototypeOf,
} = Object;

const { isArray } = Array;

const {
  filter: ArrayFilter,
  find: ArrayFind,
  indexOf: ArrayIndexOf,
  join: ArrayJoin,
  map: ArrayMap,
  push: ArrayPush,
  reduce: ArrayReduce,
  reverse: ArrayReverse,
  slice: ArraySlice,
  splice: ArraySplice,
  unshift: ArrayUnshift,
  forEach,
} = Array.prototype;

const {
  charCodeAt: StringCharCodeAt,
  replace: StringReplace,
  slice: StringSlice,
  toLowerCase: StringToLowerCase,
} = String.prototype;

export {
  ArrayFilter,
  ArrayFind,
  ArrayIndexOf,
  ArrayJoin,
  ArrayMap,
  ArrayPush,
  ArrayReduce,
  ArrayReverse,
  ArraySlice,
  ArraySplice,
  ArrayUnshift,
  assign,
  create,
  defineProperties,
  defineProperty,
  forEach,
  freeze,
  getOwnPropertyDescriptor,
  getOwnPropertyDescriptors,
  getOwnPropertyNames,
  getPrototypeOf,
  isArray,
  keys,
  seal,
  setPrototypeOf,
  StringCharCodeAt,
  StringReplace,
  StringSlice,
  StringToLowerCase,
};

export function isUndefined(obj: any): obj is undefined {
  return obj === undefined;
}

export function isNull(obj: any): obj is null {
  return obj === null;
}

export function isTrue(obj: any): obj is true {
  return obj === true;
}

export function isFalse(obj: any): obj is false {
  return obj === false;
}

export function isFunction(obj: any): obj is Function {
  return typeof obj === 'function';
}
export function isObject(obj: any): obj is object {
  return typeof obj === 'object';
}

export function isString(obj: any): obj is string {
  return typeof obj === 'string';
}

export function isNumber(obj: any): obj is number {
  return typeof obj === 'number';
}

const OtS = {}.toString;
export function toString(obj: any): string {
  if (obj && obj.toString) {
    // Arrays might hold objects with "null" prototype So using
    // Array.prototype.toString directly will cause an error Iterate through
    // all the items and handle individually.
    if (isArray(obj)) {
      return ArrayJoin.call(ArrayMap.call(obj, toString), ',');
    }
    return obj.toString();
  } else if (typeof obj === 'object') {
    return OtS.call(obj);
  } else {
    return obj + emptyString;
  }
}

export function getPropertyDescriptor(o: any, p: PropertyKey): PropertyDescriptor | undefined {
  do {
    const d = getOwnPropertyDescriptor(o, p);
    if (!isUndefined(d)) {
      return d;
    }
    o = getPrototypeOf(o);
  } while (o !== null);
  return;
}

export const emptyString = '';

export function MapConcat(maps: any[]): Map<any, any> {
  const map = new Map();

  maps.forEach((m: Map<any, any>) => {
    m.forEach((v: any, k: any) => {
      map.set(k, v);
    });
  });

  return map;
}

export const MapCreate = (args: any[]) => new Map(args);

/*
 * The __type property on HiddenField doesn't actually exists at runtime. It is only used to store
 * the type of the associated field value.
 */
export type HiddenField<T> = { __type: T };

export function createHiddenField<T = unknown>(key: string): HiddenField<T> {
  return Symbol(key) as any;
}

export function setHiddenField<T>(o: any, field: HiddenField<T>, value: T): void {
  o[field as any] = value;
}

export function getHiddenField<T>(o: any, field: HiddenField<T>): T | undefined {
  return o[field as any];
}

export function isHostElement(node: Node): boolean {
  return node instanceof HTMLElement && !!shadowRootGetter.call(node);
}

export function getShadowRootFromHostElement(elm: HTMLElement): ShadowRoot | null {
  return shadowRootGetter.call(elm);
}
